import { generateImageByPubkey } from '@/lib/pubkey'
import { randomString } from '@/lib/random'
import { cn } from '@/lib/utils'
import { registerModal, unregisterModal } from '@/services/modal-manager.service'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Image from './Image'

export default function ProfileBanner({
  pubkey,
  banner,
  className
}: {
  pubkey: string
  banner?: string
  className?: string
}) {
  return (
    <Image
      imageUrl={banner}
      imagePubkey={pubkey}
      alt={`${pubkey} banner`}
      className={cn('rounded-none', className)}
      placeholder={generateImageByPubkey(pubkey)}
      imgproxyModifiers="rs:fill:1200:400"
    />
  )
}

export function BannerWithLightbox({
  pubkey,
  banner,
  className
}: {
  pubkey: string
  banner?: string
  className?: string
}) {
  const id = useMemo(() => `profile-banner-lightbox-${randomString()}`, [])
  const defaultBanner = generateImageByPubkey(pubkey)
  const [index, setIndex] = useState(-1)
  const [lightboxSrc, setLightboxSrc] = useState(banner)

  useEffect(() => {
    if (index >= 0) {
      registerModal(id, () => {
        setIndex(-1)
      })
    } else {
      unregisterModal(id)
    }
  }, [index, id])

  return (
    <>
      <Image
        imageUrl={banner}
        imagePubkey={pubkey}
        alt={`${pubkey} banner`}
        className={cn('rounded-none', className)}
        classNames={{
          wrapper: 'cursor-zoom-in'
        }}
        placeholder={defaultBanner}
        onClick={handleBannerClick}
        onChosenUrl={setLightboxSrc}
      />
      {index >= 0 &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <Lightbox
              index={index}
              slides={[{ src: lightboxSrc ?? defaultBanner }]}
              plugins={[Zoom]}
              open={index >= 0}
              close={() => setIndex(-1)}
              controller={{
                closeOnBackdropClick: true,
                closeOnPullUp: true,
                closeOnPullDown: true
              }}
              carousel={{
                finite: true
              }}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null
              }}
              styles={{
                toolbar: { paddingTop: '2.25rem' }
              }}
            />
          </div>,
          document.body
        )}
    </>
  )

  function handleBannerClick(event: React.MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
    setIndex(0)
  }
}
